// Suivre un groupe ou une équipe (D-110, maquettes validées s01 à s11).
//  - La feuille « Suivre les Seniors filles » (tout le groupe ou une équipe engagée, trois réglages : le résultat et
//    les changements cochés, la veille du match au choix) et « Tes réglages » (Enregistrer, Ne plus suivre).
//  - Depuis la page publique : le petit compte supporter (e-mail + code, sans mot de passe, fonction follow-signup),
//    puis « ★ Tu suis… », le prochain match et les notifications de ce téléphone (sinon un e-mail le soir des matchs).
//  - Dans l'app : « ☆ Suivre » sur la page d'une équipe (avec le nombre d'abonnés, jamais les noms), une étoile par
//    groupe dans « Matchs du club », Moi › Mes suivis (ceux des enfants sont d'office).
//  - L'app de l'abonné : les matchs du club et ses suivis, rien de la vie interne ; « Rejoindre le club ».
//  - /f/<jeton> : le lien d'arrêt des e-mails, sans connexion (un clic, jamais à l'ouverture du lien).
import './suivre.css';
import {
  followerAskSupporter, followerBoard, followEmailSet, followEventIds, followMailInfo, followMailStop, followSet, followSignup, followState, followUnset,
  type FollowChoice, type FollowerMatch,
} from '../lib/follow-api';
import {
  autoWho, DEFAULT_PREF, isFollowing, listFr, manySquads, nextMatchesParts, scopeText, settingsShort, sinceText, stripText,
  type FollowGroup, type FollowState, type NextMatch,
} from '../domain/follow';
import { sendEmailCode, verifyEmailCode } from '../lib/auth';
import { enablePush, pushState, pushSupport } from '../lib/notifications';
import { supabase } from '../lib/supabase';
import type { Ctx } from './context';
import { $, $$, html, mount, openSheet, toast, type Html } from './dom';
import { fillCrests, oppCrest } from './crest';
import { time } from './format';
import { applyBrand } from './theme';

// --- La feuille : suivre, régler -------------------------------------------------------------------------------

export interface FollowSheet {
  group: FollowGroup;
  /** 'new' : « Suivre les … » ; 'edit' : mes réglages ; 'auto' : un suivi d'office (membre de l'équipe). */
  mode: 'new' | 'edit' | 'auto';
  submit: string;
  onSubmit: (c: FollowChoice, close: () => void, msg: (t: string) => void) => Promise<void>;
  onUnfollow?: (close: () => void) => Promise<void>;
}

/** « La veille des matchs, tu as déjà la convocation de Kenza. » */
const autoHint = (g: FollowGroup) => {
  const who = g.auto?.who ?? [];
  return who.length ? `La veille des matchs, tu as déjà la convocation de ${listFr(who)}.` : 'La veille des matchs, tu as déjà ta convocation.';
};

export function openFollowSheet(o: FollowSheet): void {
  const g = o.group, auto = o.mode === 'auto';
  const pref = auto ? { squad_id: null, result: g.auto?.result ?? true, changes: g.auto?.changes ?? true, eve: false } : g.follow ?? DEFAULT_PREF;
  const many = manySquads(g) && !auto;
  const title = o.mode === 'new' ? `Suivre les ${g.name}` : `Tes réglages · ${g.name}`;
  const check = (name: string, on: boolean, label: string, sub: string) => html`
    <label class="check"><input type="checkbox" name="${name}" ${on ? 'checked' : ''}><span><strong>${label}</strong><br><span class="muted small">${sub}</span></span></label>`;
  openSheet(title, html`
    <form class="form fw-form" novalidate>
      ${o.mode === 'new' ? html`<p class="fw-lead">Le score de chaque match et les changements, sur ton téléphone. Gratuit, sans pub : tu arrêtes en un geste.</p>` : ''}
      ${many ? html`<div class="fw-scope" role="radiogroup" aria-label="Quelles équipes" style="grid-template-columns: 1.4fr repeat(${g.squads.length}, 1fr)">
        <button type="button" class="fw-sc ${pref.squad_id ? '' : 'on'}" role="radio" aria-checked="${String(!pref.squad_id)}" data-sq=""><b>Tout le groupe</b><small>${listFr(g.squads.map(q => q.name))}</small></button>
        ${g.squads.map(q => html`<button type="button" class="fw-sc ${pref.squad_id === q.id ? 'on' : ''}" role="radio" aria-checked="${String(pref.squad_id === q.id)}" data-sq="${q.id}"><b>${q.name}</b><small>seulement</small></button>`)}
      </div>` : ''}
      <div class="fw-sets">
        ${check('result', pref.result, 'Le résultat', 'dès que le score est connu, une seule fois')}
        ${check('changes', pref.changes, 'Les changements', 'match déplacé ou annulé')}
        ${auto ? '' : check('eve', pref.eve, 'La veille du match', 'l’heure, la salle et l’entrée')}
      </div>
      <p class="hint">${o.mode === 'new' ? 'Plusieurs matchs le même jour : un seul message avec tous les scores.'
        : auto ? autoHint(g) : 'Le score part une fois, qu’il soit saisi par le club ou venu de la synchro FFBB.'}</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">${o.submit}</button>
      ${o.onUnfollow ? html`<button type="button" class="btn btn-danger-ghost" data-unfollow>Ne plus suivre</button>` : ''}
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, 'form')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    let squad: string | null = pref.squad_id;
    for (const b of $$<HTMLButtonElement>(f, '[data-sq]')) b.addEventListener('click', () => {
      squad = b.dataset.sq || null;
      $$(f, '[data-sq]').forEach(x => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-checked', String(on)); });
    });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const v = (n: string) => !!f.querySelector<HTMLInputElement>(`input[name="${n}"]`)?.checked;
      const c: FollowChoice = { squad, result: v('result'), changes: v('changes'), eve: auto ? false : v('eve') };
      if (!c.result && !c.changes && !c.eve) { msg(o.onUnfollow ? 'Coche au moins un réglage, ou « Ne plus suivre ».' : 'Coche au moins un réglage.'); return; }
      const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
      btn.disabled = true; msg('');
      try { await o.onSubmit(c, close, msg); } catch { msg('Impossible pour le moment. Réessaie.'); } finally { btn.disabled = false; }
    });
    $(f, '[data-unfollow]')?.addEventListener('click', async () => {
      try { await o.onUnfollow!(close); } catch { msg('Impossible pour le moment. Réessaie.'); }
    });
  });
}

/** Le mode de la feuille pour un groupe : d'office, mes réglages, ou un nouveau suivi. */
const modeOf = (g: FollowGroup): FollowSheet['mode'] => (g.auto ? 'auto' : g.follow ? 'edit' : 'new');

/** Suivre ou régler un groupe depuis l'app ; `done` redessine l'écran. */
export function openGroupSettings(g: FollowGroup, done: (s: FollowState) => void): void {
  const mode = modeOf(g);
  openFollowSheet({
    group: g, mode, submit: mode === 'new' ? 'Suivre' : 'Enregistrer',
    onSubmit: async (c, close) => {
      const s = await followSet(g.team_id, c);
      close(); toast(mode === 'new' ? `Tu suis les ${g.name}` : 'Réglages enregistrés'); done(s);
    },
    onUnfollow: mode === 'new' ? undefined : async (close) => {
      const s = await followUnset(g.team_id);
      close(); toast(mode === 'auto' ? `Plus rien des ${g.name}` : `Tu ne suis plus les ${g.name}`); done(s);
    },
  });
}

// --- Page publique : la bande du groupe, le petit compte, « ★ Tu suis » -----------------------------------------

export interface PublicFollow {
  slug: string;
  tz: string;
  /** Les matchs à venir de la page (pour « Prochain match »). */
  upcoming: { starts_at: string; team: string; group_id?: string | null; opponent: string; is_home: boolean | null; location?: string | null }[];
  /** Après un suivi : relire l'état et redessiner la bande. */
  refresh: () => Promise<void>;
}

/** La bande sous les pastilles : le groupe choisi, « ☆ Suivre » / « ★ Suivi ». */
export function stripHtml(g: FollowGroup): Html {
  const on = isFollowing(g);
  return html`<span class="fw-strip-t"><strong>${g.name}</strong><small>${stripText(g)}</small></span>
    <button type="button" class="fw-btn ${on ? 'on' : ''}" data-follow="${g.team_id}" data-guide="pub-follow">${on ? '★ Suivi' : '☆ Suivre'}</button>`;
}

const session = async () => (await supabase?.auth.getSession())?.data.session ?? null;

export function openPublicFollow(pf: PublicFollow, g: FollowGroup): void {
  const mode = modeOf(g);
  openFollowSheet({
    group: g, mode, submit: mode === 'new' ? 'Continuer' : 'Enregistrer',
    onSubmit: async (c, close) => {
      if (await session()) {
        await followSet(g.team_id, c);
        close(); await pf.refresh();
        if (mode === 'new') openFollowDone(pf, g, c); else toast('Réglages enregistrés');
      } else { close(); openAccountSheet(pf, g, c); }
    },
    onUnfollow: mode === 'new' ? undefined : async (close) => {
      await followUnset(g.team_id); close(); await pf.refresh(); toast(`Tu ne suis plus les ${g.name}`);
    },
  });
}

/** « Ton compte supporter » : e-mail, code reçu, « Valider et suivre ». */
function openAccountSheet(pf: PublicFollow, g: FollowGroup, c: FollowChoice): void {
  const scope = manySquads(g) ? scopeText(g, c.squad) : '';
  openSheet('Ton compte supporter', html`
    <div class="fw-recap"><span class="fw-bigstar" aria-hidden="true">★</span>
      <span><strong>${g.name}${scope ? ` · ${scope}` : ''}</strong><small>${settingsShort(c)}</small></span>
      <button type="button" class="link-x" data-change>changer</button></div>
    <p>Pour te prévenir, il te faut un petit compte : ton e-mail et un code, sans mot de passe.</p>
    <form class="form" id="fw-account" novalidate>
      <label class="field"><span>Ton e-mail</span><input type="email" name="email" inputmode="email" autocomplete="email" maxlength="120" placeholder="prenom@exemple.fr" required></label>
      <label class="pub-hp" aria-hidden="true">Ne pas remplir <input name="website" tabindex="-1" autocomplete="off"></label>
      <p class="fw-sent" hidden></p>
      <label class="field" data-code hidden><span>Le code reçu (6 chiffres)</span><input class="otp" name="code" inputmode="numeric" pattern="[0-9]*" maxlength="6" autocomplete="one-time-code" placeholder="••••••"></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit" data-guide="pub-code">Recevoir le code</button>
    </form>
    <p class="hint">Ton e-mail sert à te reconnaître et, sans l’app, à t’envoyer les résultats. Il n’est jamais donné aux partenaires ; tu n’entres pas dans les équipes.</p>`,
  (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#fw-account')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
    const input = (n: string) => f.querySelector<HTMLInputElement>(`input[name="${n}"]`)!;
    let sentTo: string | null = null;
    $(sheet, '[data-change]')!.addEventListener('click', () => { close(); openPublicFollow(pf, { ...g, follow: { ...c, squad_id: c.squad } }); });
    f.addEventListener('submit', async (e) => {
      e.preventDefault(); msg('');
      btn.disabled = true;
      try {
        if (!sentTo) {
          const email = input('email').value.trim().toLowerCase();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { msg('Adresse e-mail invalide.'); return; }
          const r = await followSignup(pf.slug, email, input('website').value);
          if (r === 'too_many') { msg('Trop de demandes : réessaie dans une heure.'); return; }
          if (r === 'invalid') { msg('Adresse e-mail invalide.'); return; }
          if (r !== 'ready') { msg('Envoi impossible pour le moment. Réessaie.'); return; }
          const sent = await sendEmailCode(email);
          if (!sent.ok) { msg(sent.message); return; }
          sentTo = email;
          const p = $(f, '.fw-sent')!; p.textContent = `✓ Code envoyé à ${email}`; p.hidden = false;
          ($(f, '[data-code]') as HTMLElement).hidden = false;
          input('email').readOnly = true;
          btn.textContent = 'Valider et suivre';
          input('code').focus();
          return;
        }
        const ok = await verifyEmailCode(sentTo, input('code').value);
        if (!ok.ok) { msg(ok.message); return; }
        await followSet(g.team_id, c);
        close(); await pf.refresh(); openFollowDone(pf, g, c);
      } catch { msg('Impossible pour le moment. Réessaie.'); }
      finally { btn.disabled = false; }
    });
  });
}

/** « ★ Tu suis les Seniors filles » : le prochain match, les notifications de ce téléphone (sinon l'e-mail du soir). */
async function openFollowDone(pf: PublicFollow, g: FollowGroup, c: FollowChoice): Promise<void> {
  const squadName = c.squad ? g.squads.find(q => q.id === c.squad)?.name : null;
  const next: NextMatch[] = pf.upcoming
    .filter(m => (m.group_id ? m.group_id === g.team_id : m.team === g.name || g.squads.some(q => q.name === m.team)) && (!squadName || m.team === squadName))
    .map(m => ({ starts_at: m.starts_at, versus: m.is_home === false ? `${m.team} à ${m.opponent}` : `${m.team} – ${m.opponent}`, is_home: m.is_home, location: m.location }));
  const parts = nextMatchesParts(next, pf.tz);
  const support = pushSupport();
  const state = support === 'ok' ? await pushState().catch(() => 'off' as const) : 'off';
  // Le service des notifications vit à la racine du sous-domaine du club (D-032) : pas sur /c/<adresse>.
  const canPush = support === 'ok' && state !== 'on' && state !== 'denied' && location.pathname === '/';
  openSheet(`★ Tu suis les ${g.name}`, html`
    ${parts.length ? html`<p>Prochain match : ${parts.map((p, i) => html`${i ? ', puis ' : ''}<strong>${p.versus}</strong>${p.tail}`)}.</p>`
      : html`<p>Pas de match annoncé pour l’instant : tu seras prévenu dès qu’il y en a un.</p>`}
    <section class="block push-card fw-push">
      ${state === 'on' ? html`<p><strong>Notifications activées sur ce téléphone.</strong> Le score arrive dès la fin du match.</p>`
        : html`<p><strong>Sur ce téléphone, en un geste.</strong> Le score arrive dès la fin du match.</p>
          ${canPush ? html`<button type="button" class="btn btn-primary" data-push data-guide="pub-push">Activer les notifications</button>` : ''}
          <p class="hint">Sur iPhone, ajoute d’abord l’app du club à l’écran d’accueil (Partager → « Sur l’écran d’accueil »). Sans notifications : un e-mail le soir des matchs, un seul par journée.</p>`}
    </section>
    <button type="button" class="btn btn-ghost" data-other>Suivre une autre équipe</button>`, (sheet, close) => {
    $(sheet, '[data-push]')?.addEventListener('click', async () => {
      const r = await enablePush();
      toast(r === 'on' ? 'Notifications activées' : r === 'denied' ? 'Autorisation refusée' : 'Activation impossible', r === 'on' ? 'ok' : 'ko');
      if (r === 'on') close();
    });
    $(sheet, '[data-other]')!.addEventListener('click', () => {
      close();
      document.querySelector('#pub-matchs .pub-filter')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}

// --- Dans l'app : page d'une équipe, « Matchs du club », Mes suivis -----------------------------------------------

const followersText = (n: number) => (n ? `${n} ${n > 1 ? 'abonnés' : 'abonné'}` : 'Pas encore d’abonné');

/** « ☆ Suivre » sous l'en-tête de la page d'une équipe, avec le nombre d'abonnés (jamais les noms). */
export async function renderTeamFollow(el: HTMLElement, clubId: string, teamId: string): Promise<void> {
  const st = await followState({ club: clubId }).catch(() => null);
  const g = st?.groups.find(x => x.team_id === teamId);
  if (!g) { mount(el, html``); return; }
  const on = isFollowing(g);
  mount(el, html`<div class="fw-team"><button type="button" class="fw-btn ${on ? 'on' : ''}" data-guide="team-follow">${on ? '★ Suivi' : '☆ Suivre'}</button>
    <span class="muted small">${followersText(g.followers)} · résultats et changements</span></div>`);
  $(el, '.fw-btn')!.addEventListener('click', () => openGroupSettings(g, () => void renderTeamFollow(el, clubId, teamId)));
}

const chipOrder = new Map<string, string[]>();
/** « Suivre un groupe » (Matchs du club) : une étoile par groupe ; un geste pour suivre, puis ses réglages. */
export async function renderFollowGroups(el: HTMLElement, clubId: string, onChange: () => void, links = '#/suivis'): Promise<void> {
  const st = await followState({ club: clubId }).catch(() => null);
  if (!st?.groups.length) { mount(el, html``); return; }
  const kids = st.groups.some(g => g.auto?.who.length);
  // Les groupes suivis d'abord, puis les autres, les grands d'abord (comme la page publique) ; l'ordre reste le même
  // pendant la visite (une étoile touchée ne fait pas sauter la pastille).
  const big = [...new Set(st.groups.map(g => g.category ?? ''))].reverse();
  const order = (xs: FollowGroup[]) => big.flatMap(c => xs.filter(g => (g.category ?? '') === c));
  const first = [...order(st.groups.filter(isFollowing)), ...order(st.groups.filter(g => !isFollowing(g)))].map(g => g.team_id);
  const kept = chipOrder.get(clubId) ?? [];
  const ids = [...kept.filter(id => first.includes(id)), ...first.filter(id => !kept.includes(id))];
  chipOrder.set(clubId, ids);
  const groups = ids.map(id => st.groups.find(g => g.team_id === id)!);
  mount(el, html`
    <section class="block fw-groups" data-guide="fw-groups">
      <div class="block-head"><h3>Suivre une équipe</h3><a class="link-more" href="${links}">Mes suivis ›</a></div>
      <div class="fw-gchips">${groups.map(g => {
        const on = isFollowing(g);
        return html`<button type="button" class="fw-gchip ${on ? 'on' : ''}" aria-pressed="${String(on)}" data-g="${g.team_id}"><span aria-hidden="true">${on ? '★' : '☆'}</span>${g.name}${g.auto?.who[0] ? html`<small>${g.auto.who[0]}</small>` : ''}</button>`;
      })}</div>
      <p class="hint">★ : résultat et changements.${kids ? ' Tes enfants sont suivis d’office.' : ''}</p>
    </section>`);
  for (const b of $$<HTMLButtonElement>(el, '[data-g]')) b.addEventListener('click', async () => {
    const g = st.groups.find(x => x.team_id === b.dataset.g)!;
    if (isFollowing(g)) { openGroupSettings(g, () => { void renderFollowGroups(el, clubId, onChange, links); onChange(); }); return; }
    b.disabled = true;
    try { await followSet(g.team_id, { squad: null, result: true, changes: true, eve: false }); toast(`Tu suis les ${g.name} : résultats et changements`); onChange(); }
    catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); return; }
    void renderFollowGroups(el, clubId, onChange, links);
  });
}

/** Les groupes suivis, pour la ligne « Mes suivis » de Moi. */
export async function followedNames(clubId: string): Promise<string[]> {
  const st = await followState({ club: clubId }).catch(() => null);
  return (st?.groups ?? []).filter(isFollowing).map(g => g.name);
}

/** Moi › Mes suivis : chaque suivi et ses réglages ; ceux des enfants (ou de mon équipe) sont d'office. */
export async function renderMyFollows(root: HTMLElement, ctx: Ctx, abonne = false): Promise<void> {
  const club = ctx.club;
  if (!club) { mount(root, html`<div class="empty"><p class="empty-title">Aucun club.</p></div>`); return; }
  const st = await followState({ club: club.id }).catch(() => null);
  const mine = (st?.groups ?? []).filter(g => g.follow), autos = (st?.groups ?? []).filter(g => g.auto);
  const reload = () => void renderMyFollows(root, ctx, abonne);
  const chip = (team: string, key: string, label: string, on: boolean) =>
    html`<button type="button" class="chip-toggle chip-sm" aria-pressed="${String(on)}" data-t="${team}" data-k="${key}">${on ? '✓ ' : ''}${label}</button>`;
  const item = (g: FollowGroup) => {
    const p = g.follow!;
    return html`<article class="block fw-item" data-guide="fw-item">
      <div class="fw-item-top"><span class="fw-bigstar" aria-hidden="true">★</span>
        <span class="fw-item-t"><strong>${g.name}</strong><small>${[manySquads(g) || p.squad_id ? scopeText(g, p.squad_id) : '', sinceText(p.since)].filter(Boolean).join(' · ')}</small></span>
        <button type="button" class="link-x" data-unf="${g.team_id}">Ne plus suivre</button></div>
      <div class="chip-toggles">${chip(g.team_id, 'result', 'Résultat', p.result)}${chip(g.team_id, 'changes', 'Changements', p.changes)}${chip(g.team_id, 'eve', 'Veille du match', p.eve)}</div>
    </article>`;
  };
  const autoItem = (g: FollowGroup) => {
    const a = g.auto!, off = !a.result && !a.changes;
    return html`<article class="block fw-item ${off ? 'off' : ''}" data-guide="fw-item">
      <div class="fw-item-top"><span class="fw-bigstar" aria-hidden="true">★</span>
        <span class="fw-item-t"><strong>${g.name}</strong><small>${autoWho(a)}</small></span>
        <span class="chip chip-soft">d’office</span></div>
      <div class="chip-toggles">${chip(g.team_id, 'result', 'Résultat', a.result)}${chip(g.team_id, 'changes', 'Changements', a.changes)}</div>
      <p class="hint">${autoHint(g)}</p>
    </article>`;
  };
  const email = st?.origin && !st.minor ? html`
    <div class="fw-mail">
      <label class="check"><input type="checkbox" id="fw-email" ${st.email_digest ? 'checked' : ''}><span><strong>Un e-mail le soir des matchs</strong><br>
        <span class="muted small">Seulement si ce téléphone n’a pas les notifications (iPhone sans l’app installée) : un seul par journée.</span></span></label>
    </div>` : '';
  mount(root, html`
    <section class="screen">
      ${abonne ? '' : html`<a class="back" href="#/moi" data-back>‹ Moi</a>`}
      <header class="screen-head"><p class="eyebrow">${abonne ? club.name : 'Moi'}</p><h2>Mes suivis</h2>
        <p class="muted">Les résultats et les changements des équipes que tu suis, sur ce téléphone.</p></header>
      ${mine.map(item)}${autos.map(autoItem)}
      ${mine.length || autos.length ? '' : html`<div class="empty"><p class="empty-title">Tu ne suis aucune équipe pour l’instant.</p><p>Une étoile sur une équipe, et ses résultats arrivent sur ton téléphone.</p></div>`}
      <a class="btn btn-ghost" href="${abonne ? '#/' : '#/matchs'}">☆ Suivre une autre équipe</a>
      ${email}
      <p class="hint">Plusieurs matchs le même jour : un seul message avec tous les scores. Le nombre d’abonnés d’une équipe (jamais les noms) compte dans l’audience montrée aux partenaires.</p>
    </section>`);
  for (const b of $$<HTMLButtonElement>(root, '[data-k]')) b.addEventListener('click', async () => {
    const g = st!.groups.find(x => x.team_id === b.dataset.t)!;
    const cur = g.follow ?? { squad_id: null, result: g.auto!.result, changes: g.auto!.changes, eve: false };
    const next = { ...cur, [b.dataset.k!]: b.getAttribute('aria-pressed') !== 'true' } as typeof cur;
    if (g.follow && !next.result && !next.changes && !next.eve) { toast('Pour tout couper : « Ne plus suivre ».', 'ko'); return; }
    b.disabled = true;
    try { await followSet(g.team_id, { squad: next.squad_id, result: next.result, changes: next.changes, eve: next.eve }); reload(); }
    catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-unf]')) b.addEventListener('click', async () => {
    const g = st!.groups.find(x => x.team_id === b.dataset.unf)!;
    if (!b.dataset.sure) { b.dataset.sure = '1'; b.textContent = 'Confirmer'; return; }
    try { await followUnset(g.team_id); toast(`Tu ne suis plus les ${g.name}`); reload(); } catch { toast('Impossible pour le moment.', 'ko'); }
  });
  $<HTMLInputElement>(root, '#fw-email')?.addEventListener('change', async (e) => {
    const on = (e.target as HTMLInputElement).checked;
    try { await followEmailSet(club.id, on); toast(on ? 'Un e-mail le soir des matchs' : 'Plus d’e-mail le soir des matchs'); }
    catch { (e.target as HTMLInputElement).checked = !on; toast('Impossible pour le moment.', 'ko'); }
  });
}

// --- L'app de l'abonné : les matchs du club, rien de la vie interne ----------------------------------------------

const DAY = 86400_000;
const isoIn = (days: number) => new Date(Date.now() + days * DAY).toISOString();

function followerRow(m: FollowerMatch): Html {
  const d = new Date(m.starts_at);
  const over = d.getTime() < Date.now() - 4 * 3600_000;
  const res = m.forfeit ? (m.forfeit === 'them' ? 'win' : 'loss')
    : m.score_us !== null && m.score_them !== null ? (m.score_us > m.score_them ? 'win' : m.score_us < m.score_them ? 'loss' : 'draw') : null;
  return html`
    <li class="cm-row">
      <span class="date-tile"><b>${d.getDate()}</b><small>${d.toLocaleDateString('fr-FR', { month: 'short' })}</small></span>
      <span class="cm-main">
        <span class="card-title crest-line">${oppCrest(m, 'opp-crest-xs')}<span>${m.followed ? html`<i class="fw-mini" aria-label="suivi">★</i> ` : ''}${m.team_name} ${m.is_home === false ? 'à' : '–'} ${m.opponent}</span></span>
        <span class="muted small">${d.toLocaleDateString('fr-FR', { weekday: 'long' })} ${time(d)} · ${m.is_home === true ? 'à domicile' : m.is_home === false ? 'à l’extérieur' : 'terrain neutre'}${m.location ? ` · ${m.location}` : ''}</span>
      </span>
      <span class="cm-acts">${over && res ? html`<b class="result-score ${res === 'win' ? 'win' : res === 'loss' ? 'loss' : ''}">${m.forfeit ? (res === 'win' ? 'V' : 'D') : `${m.score_us} – ${m.score_them}`}</b>` : ''}</span>
    </li>`;
}

/** L'accueil de l'abonné : « Suivre un groupe », puis les matchs du club (à venir, résultats), ★ ceux qu'il suit. */
export async function renderFollowerHome(root: HTMLElement, ctx: Ctx, view: 'next' | 'past' = 'next'): Promise<void> {
  const club = ctx.club;
  if (!club) { mount(root, html`<div class="empty"><p class="empty-title">Aucun club.</p></div>`); return; }
  const list = view === 'next' ? await followerBoard(club.id, isoIn(-4 / 24), isoIn(28)).catch(() => [] as FollowerMatch[])
    : (await followerBoard(club.id, isoIn(-28), isoIn(-4 / 24)).catch(() => [] as FollowerMatch[])).reverse();
  const reload = (v = view) => void renderFollowerHome(root, ctx, v);
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Matchs du club</h2>
        <p class="muted">Les matchs de toutes les équipes ; ★ ceux que tu suis.</p></header>
      <div id="fw-groups"></div>
      <div class="seg" role="tablist" aria-label="Période">
        <button type="button" role="tab" data-view="next" aria-selected="${String(view === 'next')}">À venir</button>
        <button type="button" role="tab" data-view="past" aria-selected="${String(view === 'past')}">Résultats</button>
      </div>
      ${list.length ? html`<ul class="cm-list">${list.map(followerRow)}</ul>`
        : html`<div class="empty"><p class="empty-title">${view === 'next' ? 'Pas de match dans les 4 semaines.' : 'Pas de résultat récent.'}</p></div>`}
    </section>`);
  for (const b of $$<HTMLButtonElement>(root, '[data-view]')) b.addEventListener('click', () => reload(b.dataset.view === 'past' ? 'past' : 'next'));
  void renderFollowGroups($(root, '#fw-groups')!, club.id, () => reload());
  void fillCrests(root);
}

/** ★ sur les matchs suivis d'une liste (« Matchs du club » des membres). */
export async function markFollowed(root: HTMLElement, clubId: string, from: string, to: string): Promise<void> {
  const ids = await followEventIds(clubId, from, to).catch(() => new Set<string>());
  for (const li of $$<HTMLElement>(root, '[data-ev]')) {
    const t = li.querySelector('.card-title > span:last-child');
    if (t && ids.has(li.dataset.ev!) && !t.querySelector('.fw-mini')) t.insertAdjacentHTML('afterbegin', '<i class="fw-mini" aria-label="suivi">★</i> ');
  }
}

/** « Rejoindre le club » (abonné) : une demande au bureau, qui l'accepte (D-064) ; le compte entre avec le rôle supporter,
 *  sa casquette est « Bénévole » (D-097). */
export function openAskSupporter(ctx: Ctx): void {
  const club = ctx.club!;
  openSheet('Rejoindre le club', html`
    <form class="form" id="fw-ask" novalidate>
      <p>Tu veux venir encourager avec le club, donner un coup de main ? Le bureau t’accueille : tu verras alors la vie du club, et tu pourras devenir bénévole.</p>
      <label class="field"><span>Ton prénom</span><input name="name" maxlength="60" autocomplete="given-name" value="${ctx.profileName ?? ''}"></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Envoyer ma demande</button>
      <p class="hint">Le bureau du ${club.name} reçoit ta demande avec ton e-mail, et t’invite en un geste.</p>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#fw-ask')!;
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const name = String(new FormData(f).get('name') ?? '').trim();
      if (!name) { $(f, '.form-msg')!.textContent = 'Indique ton prénom.'; return; }
      try {
        const fresh = await followerAskSupporter(club.id, name);
        close(); toast(fresh ? 'Demande envoyée au bureau' : 'Ta demande est déjà chez le bureau');
      } catch { $(f, '.form-msg')!.textContent = 'Envoi impossible pour le moment.'; }
    });
  });
}

// --- /f/<jeton> : le lien d'arrêt des e-mails, sans connexion ------------------------------------------------------

export async function renderFollowStop(root: HTMLElement, token: string): Promise<void> {
  const info = await followMailInfo(token).catch(() => null);
  // Aux couleurs du club (comme sa page publique), sinon d'Arceau.
  applyBrand(info ? { id: `stop:${token.slice(0, 8)}`, name: info.club, short_name: info.short_name, logo_url: info.logo, primary_color: info.color } : null, false);
  document.title = info ? `${info.club} · les e-mails des matchs` : 'Les e-mails des matchs';
  const logo = info?.logo ? html`<img class="pub-logo" src="${info.logo}" alt="">` : html`<span class="ball pu-ball" aria-hidden="true"></span>`;
  const page = (body: Html) => mount(root, html`
    <div class="pub">
      <header class="pub-head"><div class="pub-id">${logo}
        <div><h1>${info?.club ?? 'Arceau'}</h1><p>Les e-mails des matchs</p></div></div></header>
      <main class="pub-main">${body}</main>
      <footer class="pub-foot"><div><strong>${info?.club ?? 'Arceau'}</strong><small>Site du club · propulsé par Arceau</small></div></footer>
    </div>`);
  if (!info) {
    page(html`<section class="pub-thanks"><p class="pub-kicker">Lien introuvable</p><h3>Ce lien ne vaut plus</h3><p>Vérifie le lien reçu dans l’e-mail. Tes suivis se règlent aussi dans l’app : Moi › Mes suivis.</p></section>`);
    return;
  }
  const done = () => page(html`<section class="pub-thanks"><p class="pub-kicker">✓ C’est noté</p><h3>Plus d’e-mail</h3>
    <p>Tu ne recevras plus l’e-mail du soir des matchs du ${info.club}. Tes suivis restent dans l’app (Moi › Mes suivis), avec les notifications.</p></section>`);
  if (!info.on) { done(); return; }
  page(html`<section class="pub-thanks fw-stop"><p class="pub-kicker">Les e-mails des matchs</p><h3>Ne plus recevoir ces e-mails ?</h3>
    <p>Un e-mail le soir des matchs, avec les scores des équipes que tu suis au ${info.club}.</p>
    <button type="button" class="btn btn-primary" id="fw-stop">Ne plus recevoir ces e-mails</button></section>`);
  $(root, '#fw-stop')!.addEventListener('click', async (e) => {
    (e.target as HTMLButtonElement).disabled = true;
    const r = await followMailStop(token).catch(() => null);
    if (r) done(); else { (e.target as HTMLButtonElement).disabled = false; toast('Impossible pour le moment.', 'ko'); }
  });
}
